import { BlankPage } from '@whistler-ui/k2/components/BlankPage';
import { ErrorBoundaryWrapper } from '@whistler-ui/k2/components/ErrorBoundary';
import { Loading } from '@whistler-ui/k2/components/Loading';
import { SubNavigation } from '@whistler-ui/k2/components/SubNavigation';
import { cn } from '@whistler-ui/k2/helpers';
import { useRightPosition } from '@whistler-ui/k2/hooks';
import { PageRoute } from '@whistler-ui/k2/types';
import { Fragment, lazy, useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router';

import { Header } from '../components/Header';
import { Navigation } from '../components/Navigation/Navigation';
import { sessionTracker } from '../helpers/sessionTracker';
import { useRoleKeeper, useStoredPreviousUrl } from '../hooks';
import { useGetUseCasesV2 } from '../hooks/queries/useUseCasesV2';
import { signupPage } from '../services';
import { useAuthStore } from '../store';
import { useEntitlementsStore } from '../store/entitlements';
import { useThemeStore } from '../store/theme';
import { routes } from '.';
import { isInvalidOrganisation } from './helpers';
const UnauthorizedPage = lazy(() => import('../pages/UnauthorizedPage'));

const BASE_CLASSNAME = 'c-PrivateGuard';

interface Props extends PageRoute {
  routeFilters: {
    filterTestModeRoutes: ([, route]: [any, any]) => boolean;
    filterProductionModeRoutes: ([, route]: [any, any]) => boolean;
    customFilters: any[];
  };
}

const PrivateGuard = ({
  isStandAlone,
  isBlankPage,
  routeFilters,
  ...rest
}: Props) => {
  const [can] = useRoleKeeper();
  const [loading, setLoading] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);

  const { setPreviousUrlValue } = useStoredPreviousUrl();
  const { pathname } = useLocation();
  const location = useLocation();

  const { authValues } = useAuthStore();

  const navigate = useNavigate();

  const { current: env } = useEntitlementsStore();

  const { data } = useGetUseCasesV2(env?.id);

  const activeTags = data?.selectedTags || [];

  const {
    loggedUser,
    organisationState: { organisation },
  } = authValues;

  const { config } = useThemeStore();

  useRightPosition(containerRef, 'container-right-size');

  useEffect(() => {
    const initSession = async () => {
      if (!loggedUser) {
        setPreviousUrlValue(pathname, false);
        // This will redirect to PublicGoard and will do all its checks
        navigate(routes.login.url);
        setLoading(false);
        return;
      }

      sessionTracker.start();

      if (organisation) {
        const inactiveStatus = isInvalidOrganisation(organisation.status);

        if (organisation.partner !== config.context) {
          return signupPage();
        }

        if (inactiveStatus) {
          navigate(routes.underReview.url, { replace: true });
        }
      }

      setLoading(false);
    };

    initSession();
  }, [organisation, loggedUser]);

  const getFilterByActiveUseCase = (activeTags: any) => {
    return ([, route]) => {
      // If the route does not have useCases, it should be displayed
      if (!route.useCases) {
        return true;
      }

      if (activeTags) {
        // If the route has useCases and the requested tags include any of them, the route should be displayed
        return route.useCases.some(tag => {
          return activeTags.includes(tag);
        });
      }

      // If there are no requested tags, the route should not be displayed
      return false;
    };
  };

  const isUnderReview = location.pathname === routes.underReview.url;

  if (loading) {
    return (
      <div className="flex h-full">
        <Loading />
      </div>
    );
  }

  if (isBlankPage) {
    return (
      <BlankPage config={config} isStandAlone={isStandAlone}>
        <ErrorBoundaryWrapper isCentered>
          <Outlet />
        </ErrorBoundaryWrapper>
      </BlankPage>
    );
  }

  return (
    <Fragment>
      {!isUnderReview && (
        <Navigation
          routes={routes}
          routeFilters={{
            ...routeFilters,
            customFilters: [
              ...routeFilters.customFilters,
              getFilterByActiveUseCase(activeTags),
            ],
          }}
        />
      )}
      <SubNavigation />
      {/*
        `__presentation` carries no styles of its own any more, but the class
        must stay: portal's `presentation` / `presentation-padding-r-*`
        utilities target it as a sibling to reserve the right-hand panel.

        The navigation clearance deliberately does NOT live here. Both rails
        are `position: fixed`, so each full-bleed row pads itself with
        `nav-clearance` — padding sits inside that row's own background box, so
        the Header keeps painting edge to edge. Padding this shell instead
        clipped the Header's white bar and bottom border at the rail.
      */}
      <div
        className={cn(
          `${BASE_CLASSNAME}__presentation`,
          'flex w-full flex-col',
        )}
      >
        <Header />
        <div className="nav-clearance flex justify-center">
          <div className="mx-auto w-full max-w-360" ref={containerRef}>
            <div
              className={cn(
                BASE_CLASSNAME,
                'min-h-full min-w-full bg-(--color-background-primary)',
              )}
            >
              {/*
                Class kept as a hook: the print stylesheet zeroes this padding.
                The 1440px cap lives here rather than on `.container`, because
                `.container` reserves the fixed navigation's width in its own
                padding — capping it would centre it away from the nav and open
                a gap. Capping the content instead keeps the shell aligned and
                centres the content in whatever space is left.
              */}
              <div
                className={cn(
                  `${BASE_CLASSNAME}--content-container`,
                  'pt-5 pb-15',
                )}
              >
                {rest.permission && !can(rest.permission) ? (
                  <UnauthorizedPage />
                ) : (
                  <ErrorBoundaryWrapper>
                    <Outlet />
                  </ErrorBoundaryWrapper>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default PrivateGuard;
